import React from 'react';
import { ClosingCTA, Container, Eyebrow, PageHero, Section, sectionHeadingClass } from '../components/ui/Primitives';
import { StepFlow } from '../components/StepFlow';
import { FaqSection } from '../components/FaqSection';
import { Seo } from '../components/seo/Seo';
import { images, processFlow } from '../data/site';
import { faqs, pageSeo } from '../data/seo';
import { breadcrumbSchema, faqSchema, legalServiceSchema, organizationSchema, webPageSchema } from '../utils/schema';
const modelPoints = ['AI helps organize and prepare legal work.', 'Structured workflows improve consistency.', 'Lawyers review and guide the legal service.', 'The goal is a clearer, more modern client experience.'];
const whyItMatters = [{
  title: 'Better structure',
  body: 'Every matter follows a defined path.'
}, {
  title: 'More clarity',
  body: 'You always know what is happening and why.'
}, {
  title: 'Improved efficiency',
  body: 'Less time spent on repetitive groundwork.'
}, {
  title: 'A modern experience',
  body: 'Legal work that feels current, not dated.'
}, {
  title: 'Professional oversight',
  body: 'A lawyer reviews and signs off.'
}];
const seo = pageSeo.howItWorks;
const crumbs = [{
  label: 'Home',
  path: '/'
}, {
  label: 'How It Works',
  path: '/how-it-works'
}];
export function HowItWorks() {
  return <>
      <Seo title={seo.title} description={seo.description} path={seo.path} keywords={seo.keywords} image={images.abstract} schemas={[organizationSchema, legalServiceSchema, webPageSchema({
      ...seo
    }), breadcrumbSchema(crumbs), faqSchema(faqs)]} data-id="element-289" data-mp="1dy5pg72qx75n" />

      <PageHero eyebrow="The model" title="How NexLaw Works" subtitle="A modern legal model built around technology, AI, and lawyer oversight." crumbs={crumbs} data-id="element-290" data-mp="v2fjy9vltsn0" />

      {/* The model */}
      <Section ariaLabel="AI-powered legal workflows" data-id="element-291" data-mp="2pg6rn3udi2ft">
        <div className="grid gap-10 md:grid-cols-12 md:gap-16" data-id="element-292" data-mp="38js591blb8uq">
          <div className="md:col-span-6" data-id="element-293" data-mp="38i79j8djhjva">
            <Eyebrow data-id="element-294" data-mp="3dbsbtcm6100a">01 — The model</Eyebrow>
            <h2 className={`mt-6 md:mt-7 ${sectionHeadingClass}`} data-id="element-295" data-mp="3dcv2bjfkt0fo">
              AI-powered legal workflows.
            </h2>
            <ul className="mt-9 divide-y divide-line border-y border-line md:mt-10" data-id="element-296" data-mp="34i6vvpy9lcpa">
              {modelPoints.map(point => <li key={point} className="py-5 text-[16px] leading-relaxed text-graphite md:text-[16.5px]" data-id="element-297" data-mp="3s92m4in2nzuf">
                  {point}
                </li>)}
            </ul>
          </div>

          <div className="md:col-span-6" data-id="element-298" data-mp="3n8y0ke7b4exc">
            <div className="rounded-sm border border-line bg-white p-6 sm:p-8 md:p-10" data-id="element-299" data-mp="3n7v9a562oagw">
              <p className="text-[12px] uppercase tracking-[0.18em] text-mist" data-id="element-300" data-mp="26uev6lzzztcy">
                The NexLaw model
              </p>
              <div className="mt-7 space-y-4 md:mt-8" data-id="element-301" data-mp="2bv2o5d7sek9t">
                <div className="rounded-sm bg-sand px-5 py-5 sm:px-6" data-id="element-302" data-mp="249qn6vihckdx">
                  <p className="font-display text-[17px] font-medium text-ink" data-id="element-303" data-mp="1zb5pb2qpacsn">
                    Client matter
                  </p>
                  <p className="mt-1 text-[14px] text-mist" data-id="element-304" data-mp="2db6ma73uh02u">
                    The issue, the facts, the goal
                  </p>
                </div>
                <div aria-hidden="true" className="mx-auto h-8 w-px bg-line" data-id="element-305" data-mp="28a0woi5hdsxj" />
                <div className="grid gap-4 sm:grid-cols-2" data-id="element-306" data-mp="1kmix40fz5qk0">
                  <div className="rounded-sm border border-line px-5 py-5 sm:px-6" data-id="element-307" data-mp="1glgzfo1phmk5">
                    <p className="font-display text-[17px] font-medium text-ink" data-id="element-308" data-mp="1ucm26z0deuyp">
                      AI &amp; workflow
                    </p>
                    <p className="mt-1 text-[14px] text-mist" data-id="element-309" data-mp="1pjsty15p14hv">
                      Organize, structure, prepare
                    </p>
                  </div>
                  <div className="rounded-sm border border-line px-5 py-5 sm:px-6" data-id="element-310" data-mp="wxgjd3ogohus">
                    <p className="font-display text-[17px] font-medium text-ink" data-id="element-311" data-mp="1ajnoz6ajb52c">
                      Lawyer
                    </p>
                    <p className="mt-1 text-[14px] text-mist" data-id="element-312" data-mp="1fja1fdijgha3">
                      Review, judgment, advice
                    </p>
                  </div>
                </div>
                <div aria-hidden="true" className="mx-auto h-8 w-px bg-line" data-id="element-313" data-mp="1p8jt4s9gzxxo" />
                <div className="rounded-sm bg-ink px-5 py-5 text-paper sm:px-6" data-id="element-314" data-mp="1ta5dszs8xgif">
                  <p className="font-display text-[17px] font-medium" data-id="element-315" data-mp="1tc9e77qxrxdy">Legal outcome</p>
                  <p className="mt-1 text-[14px] text-paper/60" data-id="element-316" data-mp="1yb2vlb9cg8je">
                    Clear, professional, delivered
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Process flow */}
      <section aria-labelledby="process-heading" className="border-y border-line bg-sand/60" data-id="element-317" data-mp="8vd7d3bwy02l">
        <Container className="py-20 md:py-28 lg:py-32" data-id="element-318" data-mp="dtodp4301sg2">
          <Eyebrow data-id="element-319" data-mp="2r3y1oppbgzmy">02 — Process</Eyebrow>
          <h2 id="process-heading" className={`mt-6 max-w-[16ch] md:mt-7 ${sectionHeadingClass}`} data-id="element-320" data-mp="2r4zeaylzdu6s">
            From first question to final output.
          </h2>
          <StepFlow steps={processFlow} layout="list" data-id="element-321" data-mp="2ibmdlahodoa5" />
        </Container>
      </section>

      {/* Why this matters */}
      <Section ariaLabel="A modern legal experience" data-id="element-322" data-mp="2da6o968gsvly">
        <div className="grid gap-10 md:grid-cols-12 md:gap-16" data-id="element-323" data-mp="3a18ym3nh7gz8">
          <div className="md:col-span-5" data-id="element-324" data-mp="360yhbuu8h452">
            <Eyebrow data-id="element-325" data-mp="362042wkm3f2m">03 — Why this matters</Eyebrow>
            <h2 className={`mt-6 md:mt-7 ${sectionHeadingClass}`} data-id="element-326" data-mp="311c3pkahtlvq">
              A modern legal experience.
            </h2>
            <img src={images.abstract} alt="Abstract network of fine connected lines representing structured legal workflows" width={1200} height={900} loading="lazy" decoding="async" className="mt-10 hidden aspect-[4/3] w-full rounded-sm object-cover md:block" data-id="element-327" data-mp="2w3akmzd2hmk9" />
          </div>
          <ul className="md:col-span-7" data-id="element-328" data-mp="1r448wycbx3hd">
            {whyItMatters.map(item => <li key={item.title} className="grid gap-1 border-b border-line py-6 first:border-t sm:grid-cols-12 sm:gap-8" data-id="element-329" data-mp="1d4381iw2dw1w">
                <h3 className="font-display text-[19px] font-medium tracking-tight text-ink sm:col-span-5 sm:text-[20px]" data-id="element-330" data-mp="1i50onr1fei1a">
                  {item.title}
                </h3>
                <p className="text-[15.5px] leading-relaxed text-graphite sm:col-span-7" data-id="element-331" data-mp="186b6dx5mavki">
                  {item.body}
                </p>
              </li>)}
          </ul>
        </div>
      </Section>

      {/* Trust */}
      <section aria-labelledby="trust-heading" className="border-t border-line" data-id="element-332" data-mp="ycko5a033hd9">
        <Container className="py-20 md:py-28 lg:py-32" data-id="element-333" data-mp="kh8qatqmtas4">
          <div className="grid gap-6 md:grid-cols-12 md:gap-10" data-id="element-334" data-mp="pd7981t6y0l5">
            <div className="md:col-span-4" data-id="element-335" data-mp="5sgvbscgkc9m">
              <Eyebrow data-id="element-336" data-mp="5t8da0d4ubhf">04 — Trust</Eyebrow>
              <h2 id="trust-heading" className={`mt-6 md:mt-7 ${sectionHeadingClass}`} data-id="element-337" data-mp="5ua0123igmez">
                Lawyer review and oversight.
              </h2>
            </div>
            <p className="font-serif text-[23px] font-light leading-[1.34] text-ink sm:text-[27px] md:col-span-8 md:text-[34px]" data-id="element-338" data-mp="fs7nvohgkh72">
              NexLaw does not replace lawyers. It uses technology to support better
              legal service delivery.
            </p>
          </div>
        </Container>
      </section>

      <FaqSection data-id="element-339" data-mp="jq5dutj1z7bi" />

      <ClosingCTA headline="Want to see the model in practice?" body="Tell us about the matter you have in mind and we will explain how NexLaw would approach it." data-id="element-340" data-mp="yh4gzp14d3np" />
    </>;
}